<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <h2>{{ $t('auth.mustChangePassword') }}</h2>
      <p class="modal-description">{{ $t('auth.mustChangePasswordDescription') }}</p>
      
      <form @submit.prevent="handleChangePassword" class="password-form">
        <div class="form-group">
          <label>{{ $t('auth.currentPassword') }}</label>
          <input 
            v-model="oldPassword" 
            type="password" 
            required 
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('auth.newPassword') }}</label>
          <input 
            v-model="newPassword" 
            type="password" 
            required 
            minlength="8"
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('auth.confirmNewPassword') }}</label>
          <input 
            v-model="confirmPassword" 
            type="password" 
            required 
            class="form-input"
          />
        </div>
        
        <div v-if="error" class="error-message">{{ error }}</div>
        <div v-if="success" class="success-message">{{ success }}</div>
        
        <div class="button-group">
          <button type="submit" :disabled="loading" class="btn-primary">
            {{ loading ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close', 'success'])
const { t } = useI18n()
const authStore = useAuthStore()

const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const error = ref('')
const success = ref('')

async function handleChangePassword() {
  error.value = ''
  success.value = ''
  
  // Validation
  if (newPassword.value !== confirmPassword.value) {
    error.value = t('auth.passwordsDoNotMatch')
    return
  }
  
  if (newPassword.value.length < 8) {
    error.value = t('auth.passwordTooShort')
    return
  }
  
  loading.value = true
  
  try {
    const response = await authStore.makeAuthenticatedRequest('/api/users/change_password/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        old_password: oldPassword.value,
        new_password: newPassword.value
      })
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || t('auth.passwordChangeFailed'))
    }
    
    success.value = t('auth.passwordChangedSuccess')
    
    // Wait a moment to show success message, then emit success
    setTimeout(() => {
      emit('success')
    }, 1500)
    
  } catch (e) {
    error.value = e.message
  } finally {
    loading.value = false
  }
}

function close() {
  // Don't allow closing if must_change_password is true
  // User must change password first
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-description {
  color: #666;
  margin-bottom: 1.5rem;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #333;
}

.form-input {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.error-message {
  background: #fee;
  color: #c33;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.success-message {
  background: #efe;
  color: #3c3;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.button-group {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.btn-primary {
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
